/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    userInformationSchema,
    dataSharingAgreementSchema,
    requesterInformationSchema,
    thirdPartyDataRecipientInformationSchema,
    connectedProductInformationSchema,
} from "../../../validation/formValidation";
import * as yup from "yup";
import {
    createRequest,
    obtainSessionToken,
    uploadFile,
    verifyEmailToken,
} from "../../../api/apiService";
import {
    Button,
    ContentSwitcher,
    ContentSwitcherOption,
    Divider,
    NotificationBadge,
    Typography,
} from "@volvo/vcdk-react";
import styles from "./RequestForm.module.css";
import type { RequestModel } from "../../../models/RequestModel";
import type { CheckboxChangeEvent } from "@volvo/vcdk/";
import { TAB_NAMES, PRODUCT_TYPE_OPTIONS } from "../../../constants/const";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import type { ProductEnum } from "../../../models/Enums/ProductEnum";
import BrandSelection from "../BrandSelection/BrandSelection";
import log from "../../../logging/logger";
import { ensureVcdkContentSwitcherWrap } from "../Wrappers/ensureVcdkContentSwitcherWrap";

import RequestInformationTab from "./RequestInformationTab/RequestInformationTab";
import ThirdPartyDataRecipientInformationTab from "./ThirdPartyDataRecipientInformationTab/ThirdPartyDataRecipientInformationTab";
import UserInformationTab from "./UserInformationTab/UserInformationTab";
import DataSharingAgreementTab from "./DataSharingAgreementTab/DataSharingAgreementTab";
import ConnectedProductInformationTab from "./ConnectedProductInformationTab/ConnectedProductInformationTab";
import type { RequestFormProps, SubmitModalData } from "./types";

ensureVcdkContentSwitcherWrap();

const RequestForm: React.FC<RequestFormProps> = ({ onBrandSelected }) => {
    const [openIndex, setOpenIndex] = useState(0);
    const [unlockedTabs, setUnlockedTabs] = useState<number[]>([0]);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
    const [emailVerificationError, setEmailVerificationError] = useState<
        string | null
    >(null);
    const [submitModal, setSubmitModal] = useState<SubmitModalData>({
        isOpen: false,
        title: "Request sent",
        content: "Your request has been submitted successfully.",
        isError: false,
    });
    const [badgesVisible, setBadgesVisible] = useState(false);
    const [tabErrorCounts, setTabErrorCounts] = useState<number[]>([]);
    const [submitInlineError, setSubmitInlineError] = useState<string | null>(
        null,
    );
    const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(
        new Set(),
    );
    const [fileUploadErrors, setFileUploadErrors] = useState<{
        [key: string]: string;
    }>({});
    const tabNamesArray = useMemo(
        () => Object.values(TAB_NAMES) as string[],
        [],
    );
    const lastVerifiedEmailRef = useRef<string | null>(null);
    const emailVerifyTimeoutRef = useRef<number | undefined>(undefined);
    const contentSwitcherRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (tabErrorCounts.length !== tabNamesArray.length) {
            setTabErrorCounts(new Array(tabNamesArray.length).fill(0));
        }
    }, [tabNamesArray, tabErrorCounts.length]);

    const [formData, setFormData] = useState<RequestModel>({
        email: "",
        brand: "" as any,
        requesterFirstName: "",
        requesterLastName: "",
        requesterEmail: "",
        requesterIdentityDocumentAttachment: "",
        requesterIdentityDocumentAttachmentName: "",
        requesterDSA: false,
        thirdPartyDataRecipientName: "",
        thirdPartyDataRecipientCountryOfRegistration: "",
        thirdPartyDataRecipientCertificateOfRegistrationAttachment: "",
        thirdPartyDataRecipientCertificateOfRegistrationAttachmentName: "",
        thirdPartyDataRecipientSME: false,
        thirdPartyDataRecipientSMEConfirmation: false,
        thirdPartyDataRecipientGatekeeper: false,
        userInformationName: "",
        userInformationFleetName: "",
        userInformationFleetCountryOfRegistration: "",
        userInformationFleetAdminEmail: "",
        dataSharingAgreementDSA: false,
        dataSharingAgreementVG: false,
        dataSharingAgreementGDPR: false,
        productType: PRODUCT_TYPE_OPTIONS[0].value as any,
        productVIN: "",
        productChassisID: "",
    });

    // Helper to select schema for a tab
    const getSchemaForTab = (tab: string) => {
        switch (tab) {
            case TAB_NAMES.RequestInformation:
                return requesterInformationSchema;
            case TAB_NAMES.ThirdPartyDataRecipientInformation:
                return thirdPartyDataRecipientInformationSchema;
            case TAB_NAMES.UserInformation:
                return userInformationSchema;
            case TAB_NAMES.DataSharingAgreement:
                return dataSharingAgreementSchema;
            case TAB_NAMES.ConnectedProductInformation:
                return connectedProductInformationSchema;
            default:
                return undefined;
        }
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const nextData = { ...formData, [name]: value };
        setFormData(nextData);

        if (name === "requesterEmail") {
            setIsEmailVerified(false);
            setEmailVerificationError(null);
            setErrors((prev) => {
                const next = { ...prev };
                delete next.requesterEmail;
                return next;
            });
        }

        if (String(value ?? "").length > 0) {
            setErrors((prev) => {
                if (!(name in prev)) {
                    return prev;
                }
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }

        const schema = getSchemaForTab(tabNamesArray[openIndex]);
        if (!schema) {
            return;
        }

        schema
            .validateAt(name as string, nextData)
            .then(() => {
                setErrors((prev) => {
                    if (!(name in prev)) {
                        return prev;
                    }
                    const next = { ...prev };
                    delete next[name];
                    return next;
                });
            })
            .catch((err: any) => {
                if (err instanceof yup.ValidationError) {
                    setErrors((prev) => ({
                        ...prev,
                        [name]: err.message,
                    }));
                }
            });
    };

    const handleCheckboxChange = (e: CheckboxChangeEvent) => {
        const { checked } = e.detail;
        const name = (e.target as HTMLInputElement).name;

        const nextData = { ...formData, [name]: checked };
        setFormData(nextData);

        if (checked) {
            setErrors((prev) => {
                if (!(name in prev)) {
                    return prev;
                }
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }

        const schema = getSchemaForTab(tabNamesArray[openIndex]);
        if (!schema) {
            return;
        }

        schema
            .validateAt(name as string, nextData)
            .then(() => {
                setErrors((prev) => {
                    if (!(name in prev)) {
                        return prev;
                    }
                    const next = { ...prev };
                    delete next[name];
                    return next;
                });
            })
            .catch((err: any) => {
                if (err instanceof yup.ValidationError) {
                    setErrors((prev) => ({
                        ...prev,
                        [name]: err.message,
                    }));
                }
            });
    };

    const handleRadioProductSelect = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            productChassisID: "",
            productVIN: "",
            productType: value as ProductEnum,
        }));
    };

    const handleRadioSMESelect = (value: boolean) => {
        setFormData((prev) => ({
            ...prev,
            thirdPartyDataRecipientSME: value,
            thirdPartyDataRecipientSMEConfirmation: false,
        }));
        // Clear confirmation error when SME is changed
        setErrors((prev) => {
            const next = { ...prev };
            delete next.thirdPartyDataRecipientSMEConfirmation;
            return next;
        });
    };

    const handleCountryChange = (country: string) => {
        const nextData = {
            ...formData,
            thirdPartyDataRecipientCountryOfRegistration: country,
        };
        setFormData(nextData);

        if (country && country.length > 0) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next.thirdPartyDataRecipientCountryOfRegistration;
                return next;
            });
        }
    };

    const handleFleetCountryChange = (country: string) => {
        const nextData = {
            ...formData,
            userInformationFleetCountryOfRegistration: country,
        };
        setFormData(nextData);

        if (country && country.length > 0) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next.userInformationFleetCountryOfRegistration;
                return next;
            });
        }
    };

    const handleFileUpload = async (
        field: keyof RequestModel,
        url: string,
        file?: File,
    ): Promise<void> => {
        const nextData = { ...formData, [field]: url };

        setFormData((prev) => ({ ...prev, [field]: url }));

        if (url && String(url).length > 0) {
            setErrors((prev) => {
                if (!(field in prev)) {
                    return prev;
                }
                const next = { ...prev };
                delete next[field as string];
                return next;
            });
            setFileUploadErrors((prev) => {
                const next = { ...prev };
                delete next[field as string];
                return next;
            });
        }

        const schema = getSchemaForTab(tabNamesArray[openIndex]) as any;
        const hasPathInSchema =
            schema && (schema as any).fields?.[field as string];

        if (schema && hasPathInSchema) {
            try {
                await schema.validateAt(field as string, nextData);
                setErrors((prev) => {
                    if (!(field in prev)) {
                        return prev;
                    }
                    const next = { ...prev };
                    delete next[field as string];
                    return next;
                });
            } catch (err: any) {
                if (err instanceof yup.ValidationError) {
                    setErrors((prev) => ({
                        ...prev,
                        [field as string]: err.message,
                    }));
                }
            }
        }

        if (!url) {
            return;
        }

        if (
            field === "requesterIdentityDocumentAttachment" ||
            field ===
                "thirdPartyDataRecipientCertificateOfRegistrationAttachment"
        ) {
            const internalField: string =
                field === "requesterIdentityDocumentAttachment"
                    ? "RequesterIdentityDocument"
                    : "ThirdPartyDataRecipientCertificateOfRegistration";

            if (file) {
                if (!formData.requesterEmail) {
                    log.error("File upload blocked: requesterEmail is empty");
                    setFileUploadErrors((prev) => ({
                        ...prev,
                        [field as string]:
                            "Please verify your email before uploading files.",
                    }));
                    return;
                }

                setUploadingFiles((prev) => new Set(prev).add(field as string));
                setFileUploadErrors((prev) => {
                    const next = { ...prev };
                    delete next[field as string];
                    return next;
                });

                try {
                    await uploadFile(
                        internalField,
                        file,
                        formData.requesterEmail,
                    );
                } catch (error) {
                    log.error("File upload failed", error);

                    setFormData((prev) => ({
                        ...prev,
                        [field]: "",
                        [`${field}Name`]: "",
                    }));

                    setFileUploadErrors((prev) => ({
                        ...prev,
                        [field as string]:
                            "Failed to upload file. Please try again.",
                    }));

                    setErrors((prev) => {
                        const next = { ...prev };
                        delete next[`${field}Name`];
                        return next;
                    });
                } finally {
                    setUploadingFiles((prev) => {
                        const next = new Set(prev);
                        next.delete(field as string);
                        return next;
                    });
                }
            }
        }
    };

    const verifyRequesterEmail = async () => {
        if (
            lastVerifiedEmailRef.current === formData.requesterEmail &&
            isEmailVerified
        ) {
            return;
        }

        try {
            await requesterInformationSchema.validateAt(
                "requesterEmail",
                formData,
            );
            setErrors((prev) => {
                const next = { ...prev };
                delete next.requesterEmail;
                return next;
            });
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                setErrors((prev) => ({
                    ...prev,
                    requesterEmail: err.message,
                }));
            }
            setIsEmailVerified(false);
            setEmailVerificationError(null);
            return;
        }

        const token = obtainSessionToken();
        if (!token) {
            setIsEmailVerified(false);
            setEmailVerificationError("Missing verification token in URL");
            return;
        }

        try {
            setIsVerifyingEmail(true);
            await verifyEmailToken(token, formData.requesterEmail);
            setIsEmailVerified(true);
            setEmailVerificationError(null);
            lastVerifiedEmailRef.current = formData.requesterEmail;
        } catch {
            setIsEmailVerified(false);
            setEmailVerificationError(
                "Email not matching the email provided for the request",
            );
        } finally {
            setIsVerifyingEmail(false);
        }
    };

    useEffect(() => {
        if (!formData.requesterEmail) {
            window.clearTimeout(emailVerifyTimeoutRef.current);
            return;
        }
        window.clearTimeout(emailVerifyTimeoutRef.current);
        emailVerifyTimeoutRef.current = window.setTimeout(() => {
            void verifyRequesterEmail();
        }, 400);

        return () => {
            window.clearTimeout(emailVerifyTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.requesterEmail]);

    const validateRequesterEmailField = async () => {
        try {
            await requesterInformationSchema.validate(formData, {
                abortEarly: false,
            });
            setErrors((prev) => {
                const next = { ...prev };
                delete next.requesterEmail;
                return next;
            });
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                const emailErrors = err.inner
                    .filter((e) => e.path === "requesterEmail")
                    .map((e) => e.message);
                if (emailErrors.length > 0) {
                    setErrors((prev) => ({
                        ...prev,
                        requesterEmail: emailErrors.join(", "),
                    }));
                } else {
                    setErrors((prev) => {
                        const next = { ...prev };
                        delete next.requesterEmail;
                        return next;
                    });
                }
            }
        }
    };

    const handleNext = async (): Promise<boolean> => {
        const currentTab = tabNamesArray[openIndex];
        const schema = getSchemaForTab(currentTab);
        if (schema) {
            try {
                await schema.validate(formData, { abortEarly: false });
                setErrors({});
            } catch (err: any) {
                if (err instanceof yup.ValidationError) {
                    const errorObj = err.inner.reduce<Record<string, string>>(
                        (acc, curr) => {
                            if (curr.path) {
                                acc[curr.path] = curr.message;
                            }
                            return acc;
                        },
                        {},
                    );
                    setErrors(errorObj);
                }
                setUnlockedTabs((prev) =>
                    prev.filter((idx) => idx <= openIndex),
                );
                return false;
            }
        }
        if (openIndex < tabNamesArray.length - 1) {
            const newIndex = openIndex + 1;
            setOpenIndex(newIndex);
            setUnlockedTabs((prev) =>
                prev.includes(newIndex) ? prev : [...prev, newIndex],
            );
        }
        return true;
    };

    const goBack = async () => {
        if (openIndex > 0) {
            setOpenIndex((i) => (i > 0 ? i - 1 : i));
        }
    };

    const countSchemaErrors = useCallback(
        async (schema?: yup.Schema<any>, tabName?: string) => {
            if (!schema) {
                return 0;
            }

            let schemaErrorCount = 0;
            try {
                await schema.validate(formData, { abortEarly: false });
            } catch (err: any) {
                if (err instanceof yup.ValidationError) {
                    const uniquePaths = new Set(
                        err.inner
                            .map((e) => e.path)
                            .filter((p): p is string => !!p),
                    );

                    // Exclude fields that have file upload errors to avoid double counting
                    if (tabName === TAB_NAMES.RequestInformation) {
                        if (
                            fileUploadErrors.requesterIdentityDocumentAttachment
                        ) {
                            uniquePaths.delete(
                                "requesterIdentityDocumentAttachment",
                            );
                        }
                    } else if (
                        tabName === TAB_NAMES.ThirdPartyDataRecipientInformation
                    ) {
                        if (
                            fileUploadErrors.thirdPartyDataRecipientCertificateOfRegistrationAttachment
                        ) {
                            uniquePaths.delete(
                                "thirdPartyDataRecipientCertificateOfRegistrationAttachment",
                            );
                        }
                    }

                    schemaErrorCount = uniquePaths.size;
                }
            }

            let fileUploadErrorCount = 0;
            if (tabName === TAB_NAMES.RequestInformation) {
                if (fileUploadErrors.requesterIdentityDocumentAttachment) {
                    fileUploadErrorCount++;
                }
            } else if (
                tabName === TAB_NAMES.ThirdPartyDataRecipientInformation
            ) {
                if (
                    fileUploadErrors.thirdPartyDataRecipientCertificateOfRegistrationAttachment
                ) {
                    fileUploadErrorCount++;
                }
            }

            return schemaErrorCount + fileUploadErrorCount;
        },
        [formData, fileUploadErrors],
    );

    const recomputeAllTabErrors = useCallback(async (): Promise<number[]> => {
        const counts = await Promise.all(
            tabNamesArray.map((name) =>
                countSchemaErrors(getSchemaForTab(name), name),
            ),
        );
        setTabErrorCounts(counts);
        return counts;
    }, [tabNamesArray, countSchemaErrors]);

    useEffect(() => {
        if (!badgesVisible) {
            return;
        }
        void recomputeAllTabErrors();
    }, [formData, badgesVisible, recomputeAllTabErrors]);

    useEffect(() => {
        const isOnLastTab =
            tabNamesArray[openIndex] === TAB_NAMES.DataSharingAgreement;
        if (
            Object.keys(fileUploadErrors).length > 0 &&
            !badgesVisible &&
            isOnLastTab
        ) {
            setBadgesVisible(true);
        }
        if (badgesVisible) {
            void recomputeAllTabErrors();
        }
    }, [
        fileUploadErrors,
        badgesVisible,
        recomputeAllTabErrors,
        openIndex,
        tabNamesArray,
    ]);

    useEffect(() => {
        if (!badgesVisible) {
            return;
        }
        const total = (tabErrorCounts ?? []).reduce((a, b) => a + b, 0);
        if (total === 0 && submitInlineError) {
            setSubmitInlineError(null);
        }
    }, [tabErrorCounts, badgesVisible, submitInlineError]);

    const goSubmit = async () => {
        handleNext();
        setSubmitInlineError(null);

        // Check if any files are still uploading
        if (uploadingFiles.size > 0) {
            setSubmitInlineError(
                "Please wait for all files to finish uploading",
            );
            return;
        }

        // Check if there are any file upload errors
        if (Object.keys(fileUploadErrors).length > 0) {
            setSubmitInlineError(
                "Please resolve file upload errors before submitting",
            );
            return;
        }

        const counts = await recomputeAllTabErrors();
        setBadgesVisible(true);

        const totalErrors = (counts ?? tabErrorCounts).reduce(
            (a, b) => a + b,
            0,
        );
        if (totalErrors > 0) {
            setSubmitInlineError("All fields need to be filled");
            return;
        }

        const advanced = await handleNext();
        if (!advanced) {
            return;
        }

        if (!isEmailVerified) {
            setEmailVerificationError("Verify your email before submitting");
            return;
        }

        try {
            await connectedProductInformationSchema.validate(formData, {
                abortEarly: false,
            });
            setErrors({});
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                const errorObj = err.inner.reduce<Record<string, string>>(
                    (acc, curr) => {
                        if (curr.path) {
                            acc[curr.path] = curr.message;
                        }
                        return acc;
                    },
                    {},
                );
                setErrors(errorObj);
            }
            return;
        }

        const payload: RequestModel = {
            ...formData,
            email: formData.requesterEmail,
        };

        try {
            await createRequest(payload);
            setSubmitModal((prev) => ({
                ...prev,
                isOpen: true,
            }));
        } catch (err: any) {
            log.error("Error", err);
            setSubmitModal((prev) => ({
                ...prev,
                isOpen: true,
                isError: true,
                title: "Request failed",
                content:
                    err.message ||
                    "An error occurred while submitting your request.",
            }));
        }
    };

    const handleCloseSubmitModal = () => {
        setSubmitModal((prev) => ({
            ...prev,
            isOpen: false,
        }));
        window.location.assign("/");
    };

    if (!formData.brand) {
        return (
            <BrandSelection
                onBrandSelected={(brand) => {
                    setFormData((prev) => ({
                        ...prev,
                        brand: brand as any,
                    }));
                    if (onBrandSelected) {
                        return onBrandSelected(brand);
                    }
                    return true;
                }}
                onNext={() => setOpenIndex(0)}
            />
        );
    }

    return (
        <div>
            <div className={styles.RequestFormContainer}>
                <div className={styles.ContentSwitcherContainer}>
                    <ContentSwitcher
                        className={styles.ContentSwitcher}
                        ref={contentSwitcherRef as any}
                    >
                        {tabNamesArray.map((name, idx) => (
                            <ContentSwitcherOption
                                className={styles.TabOption}
                                key={idx}
                                active={openIndex === idx}
                                disabled={!unlockedTabs.includes(idx)}
                                size="large"
                                onClick={async () => {
                                    if (idx !== openIndex) {
                                        const schema = getSchemaForTab(
                                            tabNamesArray[openIndex],
                                        );
                                        if (schema) {
                                            try {
                                                await schema.validate(
                                                    formData,
                                                    { abortEarly: false },
                                                );
                                                setErrors({});
                                            } catch (err: any) {
                                                if (
                                                    err instanceof
                                                    yup.ValidationError
                                                ) {
                                                    const errorObj =
                                                        err.inner.reduce<
                                                            Record<
                                                                string,
                                                                string
                                                            >
                                                        >((acc, curr) => {
                                                            if (curr.path) {
                                                                acc[curr.path] =
                                                                    curr.message;
                                                            }
                                                            return acc;
                                                        }, {});
                                                    setErrors(errorObj);
                                                }
                                                return;
                                            }
                                        }
                                    }
                                    setOpenIndex(idx);
                                    if (!unlockedTabs.includes(idx)) {
                                        setUnlockedTabs((prev) => [
                                            ...prev,
                                            idx,
                                        ]);
                                    }
                                }}
                            >
                                {name}
                                {badgesVisible &&
                                    (tabErrorCounts[idx] ?? 0) > 0 && (
                                        <NotificationBadge
                                            className={styles.TabBadge}
                                            content={String(
                                                tabErrorCounts[idx] ?? 0,
                                            )}
                                        />
                                    )}
                            </ContentSwitcherOption>
                        ))}
                    </ContentSwitcher>
                </div>
                <form>
                    {tabNamesArray[openIndex] ===
                        TAB_NAMES.RequestInformation && (
                        <RequestInformationTab
                            formData={formData}
                            handleTextChange={handleTextChange}
                            handleCheckboxChange={handleCheckboxChange}
                            handleFileUpload={handleFileUpload}
                            onNext={handleNext}
                            errors={{ ...errors, ...fileUploadErrors }}
                            isEmailVerified={isEmailVerified}
                            emailVerificationError={
                                isVerifyingEmail
                                    ? "Verifying email..."
                                    : emailVerificationError
                            }
                            onVerifyEmail={verifyRequesterEmail}
                            onRequesterEmailClick={validateRequesterEmailField}
                            uploadingFiles={uploadingFiles}
                        />
                    )}
                    {tabNamesArray[openIndex] ===
                        TAB_NAMES.ThirdPartyDataRecipientInformation && (
                        <ThirdPartyDataRecipientInformationTab
                            formData={formData}
                            handleTextChange={handleTextChange}
                            handleCheckboxChange={handleCheckboxChange}
                            handleFileUpload={handleFileUpload}
                            handleChoiceChange={handleRadioSMESelect}
                            handleCountryChange={handleCountryChange}
                            onNext={handleNext}
                            onBack={goBack}
                            errors={{ ...errors, ...fileUploadErrors }}
                            isEmailVerified={isEmailVerified}
                            uploadingFiles={uploadingFiles}
                        />
                    )}
                    {tabNamesArray[openIndex] === TAB_NAMES.UserInformation && (
                        <UserInformationTab
                            formData={formData}
                            onNext={handleNext}
                            onBack={goBack}
                            handleTextChange={handleTextChange}
                            handleFleetCountryChange={handleFleetCountryChange}
                            errors={errors}
                        />
                    )}
                    {tabNamesArray[openIndex] ===
                        TAB_NAMES.DataSharingAgreement && (
                        <DataSharingAgreementTab
                            formData={formData}
                            onSubmit={goSubmit}
                            onBack={goBack}
                            handleCheckboxChange={handleCheckboxChange}
                            errors={errors}
                            submitError={submitInlineError}
                            isSubmitDisabled={
                                uploadingFiles.size > 0 ||
                                Object.keys(fileUploadErrors).length > 0
                            }
                        />
                    )}
                    {tabNamesArray[openIndex] ===
                        TAB_NAMES.ConnectedProductInformation && (
                        <ConnectedProductInformationTab
                            formData={formData}
                            onBack={goBack}
                            onNext={handleNext}
                            handleChoiceChange={handleRadioProductSelect}
                            handleTextChange={handleTextChange}
                            errors={errors}
                        />
                    )}
                </form>
            </div>

            {submitModal.isOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="request-submitted-title"
                    className={styles.ModalWindow}
                    onClick={handleCloseSubmitModal}
                >
                    <div
                        className={styles.ModalContent}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Typography
                            id="request-submitted-title"
                            variant="heading4"
                        >
                            {submitModal.title}
                        </Typography>
                        <Divider />
                        <Typography
                            variant="body"
                            className={
                                submitModal.isError ? styles.ModalError : ""
                            }
                        >
                            {submitModal.content}
                        </Typography>
                        <div className={styles.ModalButtons}>
                            <Button
                                variant="primary"
                                onClick={handleCloseSubmitModal}
                            >
                                OK
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RequestForm;
